import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { format } from 'date-fns'
import { useLoans } from '../../hooks/useLoans'
import { useBooks } from '../../hooks/useBooks'
import { useAuth } from '../../contexts/AuthContext'
import SafeIcon from '../../common/SafeIcon'
import BookDetailModal from '../books/BookDetailModal'
import * as FiIcons from 'react-icons/fi'
import toast from 'react-hot-toast'

const { FiBook, FiClock, FiAlertCircle, FiTrendingUp, FiCalendar, FiArrowRight, FiBookOpen } = FiIcons

const PersonalDashboard = () => {
  const { user, isStaff } = useAuth()
  const { loans, loading: loansLoading, extendLoan, refetch: refetchLoans } = useLoans(user?.id, true)
  const { returnBook, checkoutBook } = useBooks()
  
  const [selectedBook, setSelectedBook] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)

  const currentLoans = loans.filter(loan => !loan.returned_date)
  const overdueLoans = currentLoans.filter(loan => loan.isOverdue)

  // This handler is now only invoked by the modal if the user is staff/admin
  const handleReturnAction = async (bookId, loanId) => {
    setActionLoading(true)
    const res = await returnBook(bookId, loanId)
    if (res.success) {
      await refetchLoans()
      setSelectedBook(null)
    }
    setActionLoading(false)
    return res
  }

  const handleCheckoutAction = async (bookId) => {
    setActionLoading(true)
    const res = await checkoutBook(bookId, user.id)
    if (res.success) {
      await refetchLoans()
      setSelectedBook(null)
    }
    setActionLoading(false)
    return res
  }

  const getBookWithLoanContext = (book) => {
    if (!book) return null
    const activeLoan = currentLoans.find(l => l.book_id === book.id)
    return {
      ...book,
      isAvailable: !activeLoan,
      currentLoan: activeLoan
    }
  }

  if (loansLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-white rounded-3xl border-2 border-white-400 animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h2 className="text-3xl font-black text-space_indigo uppercase tracking-tight">Your Reading</h2>
          <p className="text-xs font-black text-white-300 uppercase tracking-widest mt-1">Welcome back to the library</p>
        </div>
        <div className="flex gap-2">
          <div className="bg-white-500 border-2 border-white-400 p-3 rounded-2xl flex-1 md:flex-none flex items-center justify-center">
            <span className="text-lg font-black text-space_indigo">{loans.length}</span>
            <span className="text-[10px] font-black text-white-300 uppercase tracking-widest ml-2">Total</span>
          </div>
          <div className="bg-dark_cyan-900 border-2 border-dark_cyan-800 p-3 rounded-2xl flex-1 md:flex-none flex items-center justify-center">
            <span className="text-lg font-black text-dark_cyan">{currentLoans.length}</span>
            <span className="text-[10px] font-black text-dark_cyan uppercase tracking-widest ml-2">Active</span>
          </div>
        </div>
      </div>

      {/* Critical Alerts */}
      {overdueLoans.length > 0 && (
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }} 
          animate={{ opacity: 1, scale: 1 }} 
          className="bg-hot_berry text-white rounded-3xl p-6 shadow-xl shadow-hot_berry/20 relative overflow-hidden"
        >
          <div className="absolute top-[-20%] right-[-10%] w-40 h-40 bg-white/10 rounded-full blur-3xl" />
          <div className="flex items-center mb-4">
            <SafeIcon icon={FiAlertCircle} className="w-6 h-6 mr-3" />
            <h3 className="text-lg font-black uppercase tracking-widest">Overdue Alert</h3>
          </div>
          <div className="space-y-3">
            {overdueLoans.map(loan => (
              <div 
                key={loan.id} 
                onClick={() => setSelectedBook(loan.books)}
                className="bg-white/10 backdrop-blur-md rounded-2xl p-4 flex justify-between items-center border border-white/20 cursor-pointer hover:bg-white/20 transition-all"
              >
                <span className="font-black uppercase text-xs truncate max-w-[200px]">{loan.books.title}</span>
                <span className="text-[10px] font-black uppercase tracking-widest bg-white text-hot_berry px-3 py-1 rounded-full shrink-0 ml-4">
                  {isStaff ? 'Return Now' : 'See Details'}
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Main Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Current Loans */}
        <div className="space-y-4">
          <div className="flex items-center text-space_indigo px-2">
            <SafeIcon icon={FiClock} className="w-5 h-5 mr-3 text-dark_cyan" />
            <h3 className="font-black uppercase tracking-widest text-sm">On Your Shelf</h3>
          </div>
          <div className="space-y-4">
            {currentLoans.length === 0 ? (
              <div className="bg-white rounded-3xl border-2 border-white-400 p-12 text-center">
                <SafeIcon icon={FiBook} className="w-12 h-12 text-white-400 mx-auto mb-4" />
                <p className="text-[10px] font-black text-white-300 uppercase tracking-widest">Shelf is empty</p>
              </div>
            ) : (
              currentLoans.map(loan => (
                <motion.div 
                  key={loan.id} 
                  whileHover={{ y: -2 }} 
                  onClick={() => setSelectedBook(loan.books)}
                  className="bg-white rounded-3xl border-2 border-white-400 p-5 shadow-sm group cursor-pointer"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h4 className="font-black text-space_indigo uppercase text-sm truncate leading-tight group-hover:text-dark_cyan transition-colors">{loan.books.title}</h4>
                      <div className="flex items-center mt-3 gap-4">
                        <div className="flex items-center text-white-300">
                          <SafeIcon icon={FiCalendar} className="w-3.5 h-3.5 mr-1.5" />
                          <span className="text-[10px] font-black uppercase tracking-widest">{format(new Date(loan.due_date), 'MMM d')}</span>
                        </div>
                        <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-lg ${loan.isOverdue ? 'text-hot_berry bg-hot_berry-900' : 'text-dark_cyan bg-dark_cyan-900'}`}>
                          {loan.daysUntilDue <= 0 ? 'Overdue' : `${loan.daysUntilDue} days left`}
                        </span>
                      </div>
                    </div>
                    <button 
                      onClick={(e) => { e.stopPropagation(); extendLoan(loan.id); }} 
                      className="bg-white-500 border-2 border-white-400 p-3 rounded-2xl hover:border-dark_cyan hover:text-dark_cyan transition-all"
                      title="Extend Loan"
                    >
                      <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                    </button>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>

        {/* Recent History */}
        <div className="space-y-4">
          <div className="flex items-center text-space_indigo px-2">
            <SafeIcon icon={FiTrendingUp} className="w-5 h-5 mr-3 text-hot_berry" />
            <h3 className="font-black uppercase tracking-widest text-sm">Reading History</h3>
          </div>
          <div className="bg-white rounded-3xl border-2 border-white-400 overflow-hidden shadow-sm">
            <div className="divide-y-2 divide-white-400">
              {loans.length === 0 ? (
                <div className="p-12 text-center text-[10px] font-black text-white-300 uppercase tracking-widest">No history yet</div>
              ) : (
                loans.slice(0, 8).map(loan => (
                  <div 
                    key={loan.id} 
                    onClick={() => setSelectedBook(loan.books)}
                    className="p-4 flex items-center justify-between gap-4 hover:bg-white-500 transition-colors cursor-pointer group"
                  >
                    <div className="min-w-0">
                      <p className="font-black text-space_indigo uppercase text-[11px] truncate group-hover:text-dark_cyan transition-colors">{loan.books.title}</p>
                      <p className="text-[9px] font-bold text-white-300 uppercase mt-1">{format(new Date(loan.checkout_date), 'MMM d, yyyy')}</p>
                    </div>
                    <div className={`shrink-0 px-2 py-1 rounded-lg text-[8px] font-black uppercase tracking-widest border-2 ${loan.returned_date ? 'bg-white-500 text-white-300 border-white-400' : 'bg-dark_cyan-900 text-dark_cyan border-dark_cyan-800'}`}>
                      {loan.returned_date ? 'Returned' : 'Active'}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <BookDetailModal 
        book={getBookWithLoanContext(selectedBook)}
        isOpen={!!selectedBook}
        onClose={() => setSelectedBook(null)}
        onCheckout={handleCheckoutAction}
        onReturn={handleReturnAction}
        isLoading={actionLoading}
        onLoginRedirect={() => {
          // If not staff, clicking the action button in modal triggers this
          if (!isStaff && selectedBook) {
            toast.error("Please return this volume to a library staff member for check-in.")
          }
        }} 
      />
    </div>
  )
}

export default PersonalDashboard